import { useEffect, useId, useMemo, useRef, useState } from "react";
import { usePlzPlaces } from "src/hooks/usePlzPlaces";
import { fold, foldExpanded } from "src/helpers/textFold";
import "./cityInput.scss";

/**
 * City field that follows the PLZ: a PLZ with one place fills the city by itself; a PLZ shared by several
 * places lists them when the field is focused (↑/↓ + Return, or click; typing narrows the list). A city the
 * user typed or one already saved is never replaced — only an empty one or the one filled in for the
 * previous PLZ.
 */
export function CityInput({
    plz,
    value,
    onChange,
    onBlur,
    id,
    className,
    disabled,
}: {
    plz: string;
    value: string;
    onChange: (value: string) => void;
    onBlur?: (value: string) => void;
    id?: string;
    className?: string;
    disabled?: boolean;
}) {
    const listId = useId();
    const places = usePlzPlaces(plz);
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(-1);
    // The city this field filled in itself — still replaceable when the PLZ changes.
    const filled = useRef<string | null>(null);

    useEffect(() => {
        if (disabled || places.length === 0) return;
        const replaceable = !value.trim() || value === filled.current;
        if (!replaceable || places.includes(value)) return;
        const next = places.length === 1 ? places[0] : "";
        filled.current = next || null;
        if (next !== value) onChange(next);
        // Only a new PLZ's places decide — re-running on every keystroke in the city would undo typing.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [places]);

    const options = useMemo(() => {
        if (places.length < 2 || places.includes(value)) return [];
        const tokens = value.trim().split(/\s+/).filter(Boolean);
        return places.filter((p) => tokens.every((tok) => fold(p).includes(fold(tok)) || foldExpanded(p).includes(foldExpanded(tok))));
    }, [places, value]);

    const showList = open && !disabled && options.length > 0;

    const pick = (place: string) => {
        filled.current = place;
        onChange(place);
        setOpen(false);
        setActive(-1);
    };

    return (
        <div className="city-input">
            <input
                id={id}
                className={className}
                value={value}
                disabled={disabled}
                autoComplete="off"
                role="combobox"
                aria-expanded={showList}
                aria-controls={listId}
                aria-autocomplete="list"
                aria-activedescendant={showList && active >= 0 ? `${listId}-${active}` : undefined}
                onChange={(e) => {
                    onChange(e.target.value);
                    setOpen(true);
                    setActive(-1);
                }}
                onKeyDown={(e) => {
                    if (e.key === "ArrowDown" && options.length > 0) {
                        e.preventDefault();
                        setOpen(true);
                        setActive((i) => Math.min(i + 1, options.length - 1));
                    } else if (e.key === "ArrowUp" && options.length > 0) {
                        e.preventDefault();
                        setActive((i) => Math.max(i - 1, -1));
                    } else if (e.key === "Enter" && showList && active >= 0) {
                        // Only while picking — otherwise Return keeps submitting the form as before.
                        e.preventDefault();
                        pick(options[active]);
                    } else if (e.key === "Escape" && showList) {
                        e.stopPropagation();
                        setOpen(false);
                        setActive(-1);
                    }
                }}
                onFocus={() => setOpen(true)}
                onClick={() => setOpen(true)}
                onBlur={(e) => {
                    onBlur?.(e.target.value);
                    window.setTimeout(() => setOpen(false), 150);
                }}
            />
            {showList && (
                <ul id={listId} role="listbox" className="city-input__list">
                    {options.map((place, i) => (
                        <li key={place} id={`${listId}-${i}`} role="option" aria-selected={i === active} onMouseDown={() => pick(place)}>
                            {place}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
